// The brain the viewport draws over a pack, and the mesh spaces it can place a surface run from.
//
// The wgpu engine draws an HD cortex over a pack (engines/wgpu `cortex::DEFAULT_PACKS`,
// `web::load_cortex_for`): the pack's own hemisphere surfaces when it composes (`extends`) and has
// them, in any space (a subject's own, `ixi-165-cortex-hd`, since #541), else over a pack in
// MNI152NLin2009cAsym the first of BigBrain's and the template's HD cortex this node has (decision
// 0006: BigBrain's cortex everywhere, the template's as the fallback). A surface run (a dynamics run
// with its own vertices and triangles, such as an fsaverage seizure) plays on that cortex through
// the cortex's map from the run's mesh (asset-pack v0.3 `maps`, engines/wgpu #500). A subject pack
// that composes no surfaces of its own (`ixi-165-v0`) gets none, so nothing is placed on it this way.

import type { PackInfo } from './catalogue.ts';
import { networkFailed } from './retry.ts';

export const MNI = 'MNI152NLin2009cAsym';

/** engines/wgpu `cortex::DEFAULT_PACKS`, in the order the engine takes them. */
export const CORTEX_PACKS = ['bigbrain-cortex-hd', 'mni152-2009c-cortex-hd'];

export interface ShownBrain {
  /** The pack whose hemisphere surfaces the viewport draws. */
  pack: string;
  version?: string;
  /** Its hemisphere surfaces' names, left first ("Left cerebral cortex (BigBrain)"). */
  names: string[];
  /** The meshes both hemispheres have a map from (`fsaverage`, `fsaverage5`, `fsLR-32k`). */
  maps: string[];
  /** Callout names from its label layers (BigBrain's DKT parcels), by the structure each value names. */
  short: Record<string, string>;
}

interface SurfaceLayers {
  maps?: { from?: unknown }[];
  layers?: { kind?: unknown; labels?: Record<string, { structure?: unknown; short_name?: unknown } | null> }[];
}

/** structure id → short_name over every label layer of a hemisphere's layers file. */
export function layerShortNames(files: readonly SurfaceLayers[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of files)
    for (const l of f.layers ?? []) {
      if (l?.kind !== 'label') continue;
      for (const rec of Object.values(l.labels ?? {}))
        if (typeof rec?.structure === 'string' && typeof rec.short_name === 'string' && !(rec.structure in out)) out[rec.structure] = rec.short_name;
    }
  return out;
}

interface SurfaceEntry {
  id?: string;
  name?: string;
  hemisphere?: string;
  layers?: unknown;
  alternative_of?: unknown;
}

/** A pack's hemisphere surfaces, as the engine finds them: structures with `layers` and `alternative_of`, left or right. */
export function hemisphereSurfaces(structures: unknown): { name: string; hemisphere: 'L' | 'R'; layers: string }[] {
  const list = Array.isArray(structures) ? structures : ((structures as { structures?: unknown } | null)?.structures ?? []);
  const out: { name: string; hemisphere: 'L' | 'R'; layers: string }[] = [];
  for (const e of (Array.isArray(list) ? list : []) as SurfaceEntry[]) {
    if (typeof e?.layers !== 'string' || !Array.isArray(e.alternative_of)) continue;
    if (e.hemisphere !== 'L' && e.hemisphere !== 'R') continue;
    out.push({ name: e.name ?? e.id ?? e.layers, hemisphere: e.hemisphere, layers: e.layers });
  }
  return out.sort((a, b) => a.hemisphere.localeCompare(b.hemisphere));
}

/** The ids `packs/index.json` lists: this node's packs. */
export function indexIds(index: unknown): Set<string> {
  const rows = Array.isArray(index) ? index : ((index as { packs?: unknown } | null)?.packs ?? []);
  const out = new Set<string>();
  for (const r of Array.isArray(rows) ? rows : []) {
    const id = (r as { id?: unknown } | null)?.id;
    if (typeof id === 'string') out.add(id);
  }
  return out;
}

async function json(fetchFn: typeof fetch, url: string): Promise<unknown> {
  const res = await fetchFn(url);
  if (!res.ok) throw new Error(`${url}: HTTP ${res.status}`);
  return res.json();
}

/**
 * The HD cortex the viewport draws over `pack`, with the meshes it can place surface runs from: a
 * composing pack's own surfaces, else over an MNI pack the first default this node has; null for a
 * pack with neither. Only packs the node's index lists are read, so a missing one costs no request.
 * A pack whose files can't be read is skipped, as the engine skips it; a read the network failed (twice:
 * the page's fetch tries it again, data/packs.ts) fails the load instead, as the catalogue's would, rather
 * than pass for a pack that isn't there.
 */
export async function shownBrain(assets: string, packId: string, pack: PackInfo, fetchFn: typeof fetch = fetch): Promise<ShownBrain | null> {
  const own = pack.extends?.length ? [packId] : [];
  let defaults: string[] = [];
  if (pack.space === MNI) {
    try {
      const listed = indexIds(await json(fetchFn, `${assets}packs/index.json`));
      defaults = CORTEX_PACKS.filter((id) => id !== packId && listed.has(id));
    } catch (err) {
      if (networkFailed(err)) throw err;
      // No index: only the pack's own surfaces.
    }
  }
  for (const id of [...own, ...defaults]) {
    try {
      const base = `${assets}packs/${id}/`;
      const surfaces = hemisphereSurfaces(await json(fetchFn, `${base}structures.json`));
      if (!surfaces.length) continue;
      const [info, ...layers] = await Promise.all([
        id === packId ? Promise.resolve(pack) : (json(fetchFn, `${base}pack.json`) as Promise<PackInfo>),
        ...surfaces.map((s) => json(fetchFn, `${base}${s.layers}`) as Promise<SurfaceLayers>),
      ]);
      const froms = layers.map((l) => new Set((l.maps ?? []).map((m) => m.from).filter((f): f is string => typeof f === 'string')));
      const maps = [...(froms[0] ?? [])].filter((f) => froms.every((s) => s.has(f)));
      return { pack: id, version: info.version, names: surfaces.map((s) => s.name), maps, short: layerShortNames(layers) };
    } catch (err) {
      if (networkFailed(err)) throw err;
      continue;
    }
  }
  return null;
}

/** What Methods says about the cortex packs the engine picks by default. */
const ABOUT: Record<string, { label: string; text: string }> = {
  'bigbrain-cortex-hd': {
    label: 'BigBrain’s cortex',
    text: 'BigBrain’s own cortex: one person’s brain, rebuilt from 7,404 histological sections at 20 µm (Amunts et al. 2013) and placed in the template by an affine. Group data and template models reach it by registration; they aren’t measurements of this brain.',
  },
  'mni152-2009c-cortex-hd': {
    label: 'the template’s HD cortex',
    text: 'The MNI152 2009c template’s cortex (fs_LR 164k), the fallback where BigBrain’s isn’t on this machine.',
  },
  'ixi-165-cortex-hd': {
    label: 'IXI 165’s own cortex',
    text: 'IXI 165’s own cortex: the midthickness of FastSurfer 2.5.4’s surfaces from this person’s T1 (a healthy 24-year-old man, IXI, 3 T). Runs on fsaverage reach it through their own spherical registration (FreeSurfer’s sphere.reg).',
  },
};

/** The brain in a sentence: BigBrain's cortex, the template's, or its surfaces' own name. */
export function brainLabel(b: ShownBrain): string {
  return ABOUT[b.pack]?.label ?? b.names[0]?.replace(/^Left /, '') ?? b.pack;
}

/** Methods' line on the brain, for the Cortex row. */
export function brainText(b: ShownBrain): string {
  return ABOUT[b.pack]?.text ?? b.names.join(', ');
}
